import type { CustomTheme } from '../../@types/theme';
import { getDealerSearchHashParam, getValueFromQueryString } from './append-partner-id';

/**
 * Calculates the available width for the header navigation items considering
 * the window width, left and right margins, link spacing and logo spacing.
 * @param {CustomTheme} theme - The application theme.
 * @param {number} windowWidth - The window width.
 * @param {number} mainNavLength - The number of main navigation items.
 * * @param {boolean} dealerSearch - Whether the dealer search is shown.
 * @param {boolean} showDealerContext - Whether the dealer context is shown.
 * @returns {{
 *   windowWidth: number,
 *   leftMarginPx: number,
 *   rightMarginPx: number,
 *   availableWidth: number,
 *   elementsWidth: number,
 *   isElementsOverflow: boolean,
 *   leftMarginPercent: number,
 *   rightMarginPercent: number
 * }}
 */
export function calculateWidthWithMargins(
  theme: CustomTheme,
  windowWidth: number,
  mainNavLength: number,
  dealerSearch: boolean,
  showDealerContext: boolean,
): {
  windowWidth: number;
  leftMarginPx: number;
  availableWidth: number;
  elementsWidth: number;
  isElementsOverflow: boolean;
  leftMarginPercent: number;
  rightMarginPercent: number;
} {
  const itemsAverageNumber: number = 5; // the average amount of navigation items per market
  const maxItems = mainNavLength > itemsAverageNumber ? mainNavLength - 1 : mainNavLength;
  const leftMarginPercent: number = 10;
  const rightMarginPercent: number = dealerSearch || showDealerContext ? 20 : 15;
  const estimatedLinkItemWidth: number = 185; // estimated link width, we do this as trying to get dynamic value causes performance issues

  // Calculate margin values in pixels
  const leftMarginPx: number = (windowWidth * leftMarginPercent) / 100;
  const actionMenuWidth: number = (windowWidth * rightMarginPercent) / 100;
  const linkSpacing: number = parseInt(theme.sizing[600], 10) * maxItems; // Links margin-right (+-24px)
  const mod = mainNavLength < itemsAverageNumber ? itemsAverageNumber % mainNavLength : 0;
  const additionalWidth: number = mainNavLength <= 4 ? estimatedLinkItemWidth * mod : 0;
  // Calculate available width of navigation items minus logo and action menu areas
  const availableWidth: number = windowWidth + additionalWidth - actionMenuWidth - leftMarginPx;

  // Calculate total width of navigation estimated items including item margins
  const elementsWidth: number = estimatedLinkItemWidth * maxItems + linkSpacing;

  return {
    windowWidth,
    leftMarginPx,
    availableWidth,
    elementsWidth,
    isElementsOverflow: elementsWidth > availableWidth,
    leftMarginPercent,
    rightMarginPercent,
  };
}

/**
 * Checks if the URL contains a dealer search parameter or a dealer ID query string.
 * @returns {boolean} True if the URL contains a dealer search parameter or a dealer ID query string.
 */
export function dealerSearchParams(): boolean {
  const href = (typeof window !== 'undefined' && window?.location?.href) || '';
  return (
    getDealerSearchHashParam('consumerLocations') ||
    getValueFromQueryString(href, 'dealer-id') ||
    false
  );
}
